import {
  createContext,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type {
  AddableContentType,
  AddableQuestionType,
  EditableContentUnit,
  EditableQuestion,
  EditorState,
  QuestionSectionKind,
} from './editorTypes';
import type { ImageWait } from './ImageUnitEditor';
import {
  buildQuestionDisplayList,
  getQuestionSettingsForEditor,
} from './questionAssignmentUtils';
import {
  cloneEditableQuestion,
  createDraftQuestion,
} from './questionDialogUtils';
import {
  normalizeYouTubeVideoId,
  useContentEditor,
  type AddVideoResult,
} from './useContentEditor';

export interface ChangeRecord {
  kind: 'delete' | 'insert' | 'reorder' | 'unit';
  unitId?: string;
}

const VIDEO_EDIT_TARGET_PREFIX = 'video-section:';
const QUESTION_EDIT_TARGET_PREFIX = 'question:';

function createGeneratedQuestionId(prefix: string) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getQuestionEditTargetId(
  kind: QuestionSectionKind,
  questionId: string,
) {
  return `${QUESTION_EDIT_TARGET_PREFIX}${kind}:${questionId}`;
}

function parseQuestionEditTargetId(targetId: string | null) {
  if (!targetId?.startsWith(QUESTION_EDIT_TARGET_PREFIX)) {
    return null;
  }

  const [, kind, ...questionIdParts] = targetId.split(':');
  const questionId = questionIdParts.join(':');

  if (
    (kind !== 'homework' && kind !== 'preLecture') ||
    questionId.length === 0
  ) {
    return null;
  }

  return {
    kind,
    questionId,
  } satisfies {
    kind: QuestionSectionKind;
    questionId: string;
  };
}

function cloneEditorState(state: EditorState): EditorState {
  return typeof structuredClone === 'function'
    ? structuredClone(state)
    : JSON.parse(JSON.stringify(state));
}

function areUnitsEqual(
  left: EditableContentUnit | undefined,
  right: EditableContentUnit | undefined,
) {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return left === right;
  }

  return JSON.stringify(left) === JSON.stringify(right);
}

function getEditorStateSignature(state: EditorState | null) {
  if (!state) {
    return null;
  }

  return JSON.stringify({
    baseComponent: state.baseComponent,
    contentUnits: state.contentUnits,
    preLectureQuestions: state.preLectureQuestions,
    questions: state.questions,
    videoIds: state.videoIds,
  });
}

function areEditorStatesEquivalent(
  left: EditorState | null,
  right: EditorState | null,
) {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return left === right;
  }

  if (left.contentUnits.length !== right.contentUnits.length) {
    return false;
  }

  const hasDifferentUnit = left.contentUnits.some(
    (unit, index) => !areUnitsEqual(unit, right.contentUnits[index]),
  );

  if (hasDifferentUnit) {
    return false;
  }

  return (
    getEditorStateSignature(left) === getEditorStateSignature(right)
  );
}

function areVideoIdsEqual(left: string[], right: string[]) {
  if (left === right) {
    return true;
  }

  if (left.length !== right.length) {
    return false;
  }

  return left.every((videoId, index) => videoId === right[index]);
}

function getQuestionsForKind(
  editor: ReturnType<typeof useContentEditor>,
  kind: QuestionSectionKind,
) {
  return kind === 'preLecture'
    ? editor.preLectureQuestions
    : editor.questions;
}

function findQuestionIndexById(
  questions: EditableQuestion[],
  questionId: string,
) {
  return questions.findIndex(
    (question) => question.id === questionId || question.uid === questionId,
  );
}

function createDuplicateQuestion(sourceQuestion: EditableQuestion) {
  const nextId = createGeneratedQuestionId('question');
  const clonedQuestion = cloneEditableQuestion(sourceQuestion);

  return {
    ...clonedQuestion,
    fullHash: undefined,
    id: nextId,
    isNew: true,
    rawOriginal: undefined,
    uid: undefined,
    userFreeHash: undefined,
    parts: clonedQuestion.parts?.map((part, index) => ({
      ...part,
      id: `${nextId}-part-${index + 1}-${createGeneratedQuestionId('copy')}`,
      rawOriginal: undefined,
    })),
  } satisfies EditableQuestion;
}

function getVideoEditTargetId(editorState: EditorState | null) {
  if (!editorState) {
    return null;
  }

  const targetSeed =
    editorState.videoSectionId ??
    editorState.videoSectionOwnerSectionId ??
    'default';

  return `${VIDEO_EDIT_TARGET_PREFIX}${targetSeed}`;
}

function getNavSectionKey(unit: EditableContentUnit) {
  return unit.sourceSectionId || `section-${unit.sourceSectionIndex}`;
}

function isNavParentUnit(unit: EditableContentUnit) {
  return Boolean(unit.heading?.trim()) && !unit.collapseByDefault;
}

interface NavParentRange {
  end: number;
  parentUnitId: string;
  sectionId: string;
  start: number;
}

interface MultiSelectState {
  selectMultipleMode: boolean;
  selectMultipleScope: string | null;
  selectedRowIds: Set<string>;
}

function getNavParentRanges(units: EditableContentUnit[]): NavParentRange[] {
  const ranges: NavParentRange[] = [];

  units.forEach((unit, index) => {
    if (!isNavParentUnit(unit)) {
      return;
    }

    const sectionId = getNavSectionKey(unit);
    let end = index + 1;

    while (end < units.length) {
      const nextUnit = units[end];

      if (!nextUnit || getNavSectionKey(nextUnit) !== sectionId) {
        break;
      }

      if (isNavParentUnit(nextUnit)) {
        break;
      }

      end += 1;
    }

    ranges.push({
      end,
      parentUnitId: unit.id,
      sectionId,
      start: index,
    });
  });

  return ranges;
}

function moveContentUnitRange(
  units: EditableContentUnit[],
  activeRange: NavParentRange,
  overRange: NavParentRange,
) {
  if (activeRange.start === overRange.start) {
    return units;
  }

  const activeSlice = units.slice(activeRange.start, activeRange.end);
  const remainingUnits = [
    ...units.slice(0, activeRange.start),
    ...units.slice(activeRange.end),
  ];
  const insertIndex =
    activeRange.start < overRange.start
      ? overRange.end - activeSlice.length
      : overRange.start;

  remainingUnits.splice(insertIndex, 0, ...activeSlice);
  return remainingUnits;
}

export function useContentEditModeController({
  canEdit,
  initialEditorState,
}: {
  canEdit: boolean;
  initialEditorState: EditorState | null;
}) {
  const editor = useContentEditor(initialEditorState);
  const adoptedExternalStateSignatureRef = useRef(
    getEditorStateSignature(initialEditorState),
  );
  const baselineRef = useRef<EditorState | null>(null);
  const lastSavedQuestionTargetRef = useRef<string | null>(null);
  const recentlySavedQuestionTargetsRef = useRef<Set<string>>(new Set());
  const unitDraftsRef = useRef<Map<string, EditableContentUnit>>(new Map());
  // The units whose image is not ready: still uploading, or its resizable copy
  // still being made. Committed then, a unit was saved without its image, or
  // without its copy for good (#94), so leaving the open one is refused.
  const unitWaitsRef = useRef<Map<string, ImageWait>>(new Map());
  const videoDraftRef = useRef<string[] | null>(null);
  const [pageEditMode, setPageEditMode] = useState(false);
  const [activeEditUnitId, setActiveEditUnitId] = useState<string | null>(null);
  const [unitDraftsRevision, setUnitDraftsRevision] = useState(0);
  const [videoDraftRevision, setVideoDraftRevision] = useState(0);
  // How often leaving the open unit was refused, for its card to show why.
  const [leaveRefusals, setLeaveRefusals] = useState(0);
  const [localChanges, setLocalChanges] = useState<Map<string, ChangeRecord>>(
    () => new Map(),
  );
  const [multiSelectState, setMultiSelectState] = useState<MultiSelectState>(
    () => ({
      selectMultipleMode: false,
      selectMultipleScope: null,
      selectedRowIds: new Set<string>(),
    }),
  );
  const videoEditTargetId = useMemo(
    () => getVideoEditTargetId(editor.editorState),
    [editor.editorState],
  );
  const editorRef = useRef(editor);
  const canEditRef = useRef(canEdit);
  const pageEditModeRef = useRef(pageEditMode);
  const activeEditUnitIdRef = useRef(activeEditUnitId);
  const localChangesSizeRef = useRef(localChanges.size);
  const multiSelectStateRef = useRef(multiSelectState);
  const videoEditTargetIdRef = useRef(videoEditTargetId);

  const rememberBaseline = useCallback(() => {
    const currentEditorState = editorRef.current.editorState;

    if (!baselineRef.current && currentEditorState) {
      baselineRef.current = cloneEditorState(currentEditorState);
    }
  }, []);

  const setPageEditModeTracked = useCallback((nextMode: boolean) => {
    pageEditModeRef.current = nextMode;
    setPageEditMode(nextMode);
  }, []);

  const setActiveEditUnitIdTracked = useCallback((nextUnitId: string | null) => {
    activeEditUnitIdRef.current = nextUnitId;
    setActiveEditUnitId(nextUnitId);
  }, []);

  const setMultiSelectStateTracked = useCallback((
    updater: MultiSelectState | ((currentState: MultiSelectState) => MultiSelectState),
  ) => {
    setMultiSelectState((currentState) => {
      const nextState =
        typeof updater === 'function' ? updater(currentState) : updater;

      multiSelectStateRef.current = nextState;
      return nextState;
    });
  }, []);

  useEffect(() => {
    editorRef.current = editor;
    canEditRef.current = canEdit;
    pageEditModeRef.current = pageEditMode;
    activeEditUnitIdRef.current = activeEditUnitId;
    localChangesSizeRef.current = localChanges.size;
    multiSelectStateRef.current = multiSelectState;
    videoEditTargetIdRef.current = videoEditTargetId;
  }, [
    activeEditUnitId,
    canEdit,
    editor,
    localChanges.size,
    multiSelectState,
    pageEditMode,
    videoEditTargetId,
  ]);

  useEffect(() => {
    if (pageEditMode) {
      rememberBaseline();
    }
  }, [pageEditMode, rememberBaseline]);

  useEffect(() => {
    const initialEditorStateSignature =
      getEditorStateSignature(initialEditorState);

    if (
      !initialEditorState ||
      initialEditorStateSignature === adoptedExternalStateSignatureRef.current
    ) {
      return;
    }

    if (areEditorStatesEquivalent(initialEditorState, editor.editorState)) {
      adoptedExternalStateSignatureRef.current = initialEditorStateSignature;
      return;
    }

    const hasOpenDrafts = unitDraftsRef.current.size > 0;
    const canAdoptExternalState =
      activeEditUnitId === null &&
      localChanges.size === 0 &&
      !editor.isDirty &&
      !hasOpenDrafts;

    if (!canAdoptExternalState) {
      return;
    }

    editor.replaceState(cloneEditorState(initialEditorState));
    adoptedExternalStateSignatureRef.current = initialEditorStateSignature;
    baselineRef.current = pageEditMode
      ? cloneEditorState(initialEditorState)
      : null;
  }, [
    activeEditUnitId,
    editor.isDirty,
    editor.replaceState,
    initialEditorState,
    localChanges.size,
    pageEditMode,
    unitDraftsRevision,
  ]);

  const openEditMode = useCallback(() => {
    if (!canEditRef.current) {
      return false;
    }

    rememberBaseline();
    setPageEditModeTracked(true);
    return true;
  }, [rememberBaseline, setPageEditModeTracked]);

  const bumpUnitDraftsRevision = useCallback(() => {
    setUnitDraftsRevision((currentRevision) => currentRevision + 1);
  }, []);

  const bumpVideoDraftRevision = useCallback(() => {
    setVideoDraftRevision((currentRevision) => currentRevision + 1);
  }, []);

  const clearActiveDraft = useCallback((unitId: string) => {
    if (!unitDraftsRef.current.has(unitId)) {
      return;
    }

    unitDraftsRef.current.delete(unitId);
    bumpUnitDraftsRevision();
  }, [bumpUnitDraftsRevision]);

  const clearActiveEditState = useCallback(() => {
    lastSavedQuestionTargetRef.current = null;
    recentlySavedQuestionTargetsRef.current.clear();
    unitDraftsRef.current = new Map();
    videoDraftRef.current = null;
    bumpUnitDraftsRevision();
    bumpVideoDraftRevision();
    setActiveEditUnitIdTracked(null);
  }, [
    bumpUnitDraftsRevision,
    bumpVideoDraftRevision,
    setActiveEditUnitIdTracked,
  ]);

  const clearMultiSelect = useCallback((scope?: string) => {
    setMultiSelectStateTracked((currentState) => {
      if (scope && currentState.selectMultipleScope !== scope) {
        return currentState;
      }

      if (
        !currentState.selectMultipleMode &&
        currentState.selectMultipleScope === null &&
        currentState.selectedRowIds.size === 0
      ) {
        return currentState;
      }

      return {
        selectMultipleMode: false,
        selectMultipleScope: null,
        selectedRowIds: new Set<string>(),
      };
    });
  }, [setMultiSelectStateTracked]);

  const enterMultiSelect = useCallback((scope: string, initialRowId: string) => {
    setActiveEditUnitIdTracked(null);
    setMultiSelectStateTracked({
      selectMultipleMode: true,
      selectMultipleScope: scope,
      selectedRowIds: new Set([initialRowId]),
    });
  }, [setActiveEditUnitIdTracked, setMultiSelectStateTracked]);

  const toggleMultiSelectRow = useCallback((scope: string, rowId: string) => {
    setMultiSelectStateTracked((currentState) => {
      if (
        !currentState.selectMultipleMode ||
        currentState.selectMultipleScope !== scope
      ) {
        return {
          selectMultipleMode: true,
          selectMultipleScope: scope,
          selectedRowIds: new Set([rowId]),
        };
      }

      const selectedRowIds = new Set(currentState.selectedRowIds);

      if (selectedRowIds.has(rowId)) {
        selectedRowIds.delete(rowId);
      } else {
        selectedRowIds.add(rowId);
      }

      return {
        selectMultipleMode: true,
        selectMultipleScope: scope,
        selectedRowIds,
      };
    });
  }, [setMultiSelectStateTracked]);

  const isMultiSelectActiveForScope = useCallback(
    (scope: string) =>
      multiSelectStateRef.current.selectMultipleMode &&
      multiSelectStateRef.current.selectMultipleScope === scope,
    [],
  );

  const validEditableTargetIds = useMemo(() => {
    const ids = new Set(editor.contentUnits.map((unit) => unit.id));

    if (videoEditTargetId) {
      ids.add(videoEditTargetId);
    }

    editor.questions.forEach((question) => {
      ids.add(getQuestionEditTargetId('homework', question.id));

      if (question.uid) {
        ids.add(getQuestionEditTargetId('homework', question.uid));
      }
    });
    editor.preLectureQuestions.forEach((question) => {
      ids.add(getQuestionEditTargetId('preLecture', question.id));

      if (question.uid) {
        ids.add(getQuestionEditTargetId('preLecture', question.uid));
      }
    });

    return ids;
  }, [
    editor.contentUnits,
    editor.preLectureQuestions,
    editor.questions,
    videoEditTargetId,
  ]);

  useEffect(() => {
    if (activeEditUnitId === null) {
      return;
    }

    if (validEditableTargetIds.has(activeEditUnitId)) {
      return;
    }

    console.warn(
      `activeEditUnitId "${activeEditUnitId}" does not match any editable target; clearing stale edit state.`,
    );
    unitDraftsRef.current.delete(activeEditUnitId);
    if (activeEditUnitId === videoEditTargetId) {
      videoDraftRef.current = null;
      bumpVideoDraftRevision();
    }
    bumpUnitDraftsRevision();
    setActiveEditUnitIdTracked(null);
  }, [
    activeEditUnitId,
    bumpUnitDraftsRevision,
    bumpVideoDraftRevision,
    setActiveEditUnitIdTracked,
    validEditableTargetIds,
    videoEditTargetId,
  ]);

  const setUnitDraft = useCallback(
    (unitId: string, nextDraft: EditableContentUnit) => {
      const sourceUnit = editorRef.current.contentUnits.find(
        (candidate) => candidate.id === unitId,
      );
      const previousDraft = unitDraftsRef.current.get(unitId);
      const previousHasChanges = !areUnitsEqual(previousDraft, sourceUnit);
      const nextHasChanges = !areUnitsEqual(nextDraft, sourceUnit);

      unitDraftsRef.current.set(unitId, nextDraft);

      if (
        previousDraft === undefined ||
        previousHasChanges !== nextHasChanges
      ) {
        bumpUnitDraftsRevision();
      }
    },
    [bumpUnitDraftsRevision],
  );

  /** Told by a unit's card what its image waits for, if anything. */
  const setUnitWait = useCallback((unitId: string, wait: ImageWait | null) => {
    if (wait) {
      unitWaitsRef.current.set(unitId, wait);
    } else {
      unitWaitsRef.current.delete(unitId);
    }
  }, []);

  const markUnitChanged = useCallback((unitId: string, kind: ChangeRecord['kind']) => {
    setLocalChanges((currentChanges) => {
      const nextChanges = new Map(currentChanges);
      nextChanges.set(unitId, { kind, unitId });
      localChangesSizeRef.current = nextChanges.size;
      return nextChanges;
    });
  }, []);

  const getVideoDraftIds = useCallback(
    () => videoDraftRef.current ?? editorRef.current.videoIds,
    [],
  );

  const setVideoDraft = useCallback(
    (nextVideoIds: string[]) => {
      videoDraftRef.current = [...nextVideoIds];
      bumpVideoDraftRevision();
    },
    [bumpVideoDraftRevision],
  );

  const addVideoToDraft = useCallback(
    (value: string): AddVideoResult => {
      const normalizedVideoId = normalizeYouTubeVideoId(value);

      if (!normalizedVideoId) {
        return {
          error: 'Enter a YouTube video ID or a supported YouTube URL.',
          success: false,
        };
      }

      setVideoDraft([...getVideoDraftIds(), normalizedVideoId]);
      return { success: true };
    },
    [getVideoDraftIds, setVideoDraft],
  );

  const removeVideoFromDraft = useCallback(
    (index: number) => {
      const currentVideoIds = getVideoDraftIds();

      if (index < 0 || index >= currentVideoIds.length) {
        return;
      }

      setVideoDraft(
        currentVideoIds.filter((_, currentIndex) => currentIndex !== index),
      );
    },
    [getVideoDraftIds, setVideoDraft],
  );

  const reorderVideoDraft = useCallback(
    (fromIndex: number, toIndex: number) => {
      const currentVideoIds = getVideoDraftIds();

      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= currentVideoIds.length ||
        toIndex >= currentVideoIds.length
      ) {
        return;
      }

      const nextVideoIds = [...currentVideoIds];
      const [movedVideoId] = nextVideoIds.splice(fromIndex, 1);

      if (!movedVideoId) {
        return;
      }

      nextVideoIds.splice(toIndex, 0, movedVideoId);
      setVideoDraft(nextVideoIds);
    },
    [getVideoDraftIds, setVideoDraft],
  );

  const commitVideoDraft = useCallback(() => {
    const currentVideoEditTargetId = videoEditTargetIdRef.current;

    if (
      !currentVideoEditTargetId ||
      activeEditUnitIdRef.current !== currentVideoEditTargetId
    ) {
      return false;
    }

    const draft = getVideoDraftIds();
    const currentEditor = editorRef.current;

    if (!areVideoIdsEqual(draft, currentEditor.videoIds)) {
      currentEditor.replaceVideoIds(draft);
      markUnitChanged(currentVideoEditTargetId, 'unit');
    }

    videoDraftRef.current = null;
    bumpVideoDraftRevision();
    setActiveEditUnitIdTracked(null);
    return true;
  }, [
    bumpVideoDraftRevision,
    getVideoDraftIds,
    markUnitChanged,
    setActiveEditUnitIdTracked,
  ]);

  const commitActiveUnit = useCallback((explicitDraft?: EditableContentUnit) => {
    const currentActiveEditUnitId = activeEditUnitIdRef.current;

    if (!currentActiveEditUnitId) {
      return false;
    }

    if (parseQuestionEditTargetId(currentActiveEditUnitId)) {
      return false;
    }

    if (
      videoEditTargetIdRef.current &&
      currentActiveEditUnitId === videoEditTargetIdRef.current
    ) {
      return commitVideoDraft();
    }

    // Every way of leaving the unit comes through here: Save unit, and the
    // other units', the video's, the questions' and the dividers' controls,
    // which save the open unit on the way. None may while its image waits.
    if (unitWaitsRef.current.has(currentActiveEditUnitId)) {
      setLeaveRefusals((count) => count + 1);
      return false;
    }

    const draft =
      explicitDraft ?? unitDraftsRef.current.get(currentActiveEditUnitId);

    if (draft) {
      const currentEditor = editorRef.current;
      const currentUnit = currentEditor.contentUnits.find(
        (candidate) => candidate.id === currentActiveEditUnitId,
      );
      const changeKind = draft.isNew || currentUnit?.isNew ? 'insert' : 'unit';

      currentEditor.updateUnit(currentActiveEditUnitId, {
        ...draft,
        isNew: false,
      });
      markUnitChanged(currentActiveEditUnitId, changeKind);
    }

    clearActiveDraft(currentActiveEditUnitId);
    setActiveEditUnitIdTracked(null);
    return true;
  }, [
    clearActiveDraft,
    commitVideoDraft,
    markUnitChanged,
    setActiveEditUnitIdTracked,
  ]);

  const cancelActiveUnit = useCallback(() => {
    const currentActiveEditUnitId = activeEditUnitIdRef.current;

    if (!currentActiveEditUnitId) {
      return false;
    }

    if (parseQuestionEditTargetId(currentActiveEditUnitId)) {
      setActiveEditUnitIdTracked(null);
      return true;
    }

    if (
      videoEditTargetIdRef.current &&
      currentActiveEditUnitId === videoEditTargetIdRef.current
    ) {
      videoDraftRef.current = null;
      bumpVideoDraftRevision();
      setActiveEditUnitIdTracked(null);
      return true;
    }

    const draft = unitDraftsRef.current.get(currentActiveEditUnitId);

    if (draft?.isNew) {
      editorRef.current.deleteUnit(currentActiveEditUnitId);
      markUnitChanged(currentActiveEditUnitId, 'delete');
    }

    clearActiveDraft(currentActiveEditUnitId);
    setActiveEditUnitIdTracked(null);
    return true;
  }, [
    bumpVideoDraftRevision,
    clearActiveDraft,
    markUnitChanged,
    setActiveEditUnitIdTracked,
  ]);

  const autoSaveAndOpenUnit = useCallback(
    (unitId: string) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return false;
      }

      const currentActiveEditUnitId = activeEditUnitIdRef.current;

      if (currentActiveEditUnitId && currentActiveEditUnitId !== unitId) {
        if (parseQuestionEditTargetId(currentActiveEditUnitId)) {
          return false;
        }

        if (!commitActiveUnit()) {
          return false;
        }
      }

      const unit = editorRef.current.contentUnits.find(
        (candidate) => candidate.id === unitId,
      );

      if (unit) {
        setUnitDraft(unitId, unit);
        setActiveEditUnitIdTracked(unitId);
        return true;
      }

      console.warn(
        `Cannot open content unit "${unitId}" for edit because it does not exist in the current content unit list.`,
      );
      setActiveEditUnitIdTracked(null);
      return false;
    },
    [
      commitActiveUnit,
      openEditMode,
      setActiveEditUnitIdTracked,
      setUnitDraft,
    ],
  );

  const openVideoEdit = useCallback(() => {
    const currentVideoEditTargetId = videoEditTargetIdRef.current;

    if (!currentVideoEditTargetId) {
      return false;
    }

    if (!pageEditModeRef.current && !openEditMode()) {
      return false;
    }

    const currentActiveEditUnitId = activeEditUnitIdRef.current;

    if (
      currentActiveEditUnitId &&
      currentActiveEditUnitId !== currentVideoEditTargetId
    ) {
      if (parseQuestionEditTargetId(currentActiveEditUnitId)) {
        return false;
      }

      if (!commitActiveUnit()) {
        return false;
      }
    }

    videoDraftRef.current = [...editorRef.current.videoIds];
    bumpVideoDraftRevision();
    setActiveEditUnitIdTracked(currentVideoEditTargetId);
    return true;
  }, [
    bumpVideoDraftRevision,
    commitActiveUnit,
    openEditMode,
    setActiveEditUnitIdTracked,
  ]);

  const openQuestionEdit = useCallback(
    (kind: QuestionSectionKind, questionId: string) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return false;
      }

      const targetId = getQuestionEditTargetId(kind, questionId);

      lastSavedQuestionTargetRef.current = null;
      recentlySavedQuestionTargetsRef.current.delete(targetId);

      const currentActiveEditUnitId = activeEditUnitIdRef.current;

      if (currentActiveEditUnitId === targetId) {
        return true;
      }

      if (currentActiveEditUnitId) {
        if (parseQuestionEditTargetId(currentActiveEditUnitId)) {
          return false;
        }

        if (!commitActiveUnit()) {
          return false;
        }
      }

      const questions = getQuestionsForKind(editorRef.current, kind);
      const questionIndex = findQuestionIndexById(questions, questionId);

      if (questionIndex < 0) {
        console.warn(
          `Cannot open ${kind} question "${questionId}" for edit because it does not exist in the current question list.`,
        );
        setActiveEditUnitIdTracked(null);
        return false;
      }

      setActiveEditUnitIdTracked(
        getQuestionEditTargetId(kind, questions[questionIndex].id),
      );
      return true;
    },
    [
      commitActiveUnit,
      openEditMode,
      setActiveEditUnitIdTracked,
    ],
  );

  const saveActiveQuestion = useCallback(
    (nextQuestion: EditableQuestion) => {
      const activeQuestionTarget = parseQuestionEditTargetId(
        activeEditUnitIdRef.current,
      );

      if (!activeQuestionTarget) {
        return false;
      }

      const currentEditor = editorRef.current;
      const questions = getQuestionsForKind(
        currentEditor,
        activeQuestionTarget.kind,
      );
      const questionIndex = findQuestionIndexById(
        questions,
        activeQuestionTarget.questionId,
      );

      if (questionIndex < 0) {
        console.warn(
          `Cannot save ${activeQuestionTarget.kind} question "${activeQuestionTarget.questionId}" because it does not exist in the current question list.`,
        );
        setActiveEditUnitIdTracked(null);
        return false;
      }

      const currentQuestion = questions[questionIndex];
      const savedQuestion = {
        ...nextQuestion,
        isNew: false,
      };
      const activeTargetId = getQuestionEditTargetId(
        activeQuestionTarget.kind,
        activeQuestionTarget.questionId,
      );
      const targetId = getQuestionEditTargetId(
        activeQuestionTarget.kind,
        savedQuestion.id,
      );

      currentEditor.updateQuestion(
        activeQuestionTarget.kind,
        questionIndex,
        savedQuestion,
      );
      recentlySavedQuestionTargetsRef.current.add(activeTargetId);
      recentlySavedQuestionTargetsRef.current.add(targetId);
      lastSavedQuestionTargetRef.current = activeTargetId;
      markUnitChanged(
        targetId,
        currentQuestion?.isNew || nextQuestion.isNew ? 'insert' : 'unit',
      );
      setActiveEditUnitIdTracked(null);
      return true;
    },
    [markUnitChanged, setActiveEditUnitIdTracked],
  );

  const cancelActiveQuestion = useCallback(() => {
    const activeQuestionTarget = parseQuestionEditTargetId(
      activeEditUnitIdRef.current,
    );

    if (!activeQuestionTarget) {
      const lastSavedTargetId = lastSavedQuestionTargetRef.current;

      if (
        lastSavedTargetId &&
        recentlySavedQuestionTargetsRef.current.delete(lastSavedTargetId)
      ) {
        lastSavedQuestionTargetRef.current = null;
        setActiveEditUnitIdTracked(null);
        return true;
      }

      return false;
    }

    const targetId = getQuestionEditTargetId(
      activeQuestionTarget.kind,
      activeQuestionTarget.questionId,
    );

    if (recentlySavedQuestionTargetsRef.current.delete(targetId)) {
      lastSavedQuestionTargetRef.current = null;
      setActiveEditUnitIdTracked(null);
      return true;
    }

    const currentEditor = editorRef.current;
    const questions = getQuestionsForKind(
      currentEditor,
      activeQuestionTarget.kind,
    );
    const questionIndex = findQuestionIndexById(
      questions,
      activeQuestionTarget.questionId,
    );
    const currentQuestion = questionIndex >= 0
      ? questions[questionIndex]
      : undefined;

    if (currentQuestion?.isNew) {
      currentEditor.removeQuestion(activeQuestionTarget.kind, questionIndex, {
        markDirty: localChangesSizeRef.current > 0,
      });
    }

    setActiveEditUnitIdTracked(null);
    return true;
  }, [setActiveEditUnitIdTracked]);

  const insertQuestionAt = useCallback(
    (
      kind: QuestionSectionKind,
      index: number,
      type: AddableQuestionType = 'MULTIPLE_CHOICE',
    ) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return null;
      }

      const currentActiveEditUnitId = activeEditUnitIdRef.current;

      if (currentActiveEditUnitId) {
        if (parseQuestionEditTargetId(currentActiveEditUnitId)) {
          return null;
        }

        if (!commitActiveUnit()) {
          return null;
        }
      }

      const draftQuestion = createDraftQuestion(type);

      editorRef.current.addQuestion(kind, draftQuestion, index, {
        markDirty: false,
      });
      setActiveEditUnitIdTracked(
        getQuestionEditTargetId(kind, draftQuestion.id),
      );
      return draftQuestion.id;
    },
    [
      commitActiveUnit,
      openEditMode,
      setActiveEditUnitIdTracked,
    ],
  );

  const deleteQuestion = useCallback(
    (kind: QuestionSectionKind, questionId: string) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return false;
      }

      if (activeEditUnitIdRef.current) {
        return false;
      }

      const currentEditor = editorRef.current;
      const questions = getQuestionsForKind(currentEditor, kind);
      const questionIndex = findQuestionIndexById(questions, questionId);

      if (questionIndex < 0) {
        return false;
      }

      currentEditor.removeQuestion(kind, questionIndex);
      markUnitChanged(getQuestionEditTargetId(kind, questionId), 'delete');
      return true;
    },
    [markUnitChanged, openEditMode],
  );

  const deleteQuestions = useCallback(
    (kind: QuestionSectionKind, questionIds: string[]) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return false;
      }

      if (activeEditUnitIdRef.current) {
        return false;
      }

      const currentEditor = editorRef.current;
      const questions = getQuestionsForKind(currentEditor, kind);
      const deletionTargets = questionIds
        .map((questionId) => ({
          index: findQuestionIndexById(questions, questionId),
          questionId,
        }))
        .filter((target) => target.index >= 0)
        .sort((left, right) => right.index - left.index);

      if (deletionTargets.length === 0) {
        return false;
      }

      deletionTargets.forEach((target) => {
        currentEditor.removeQuestion(kind, target.index);
        markUnitChanged(
          getQuestionEditTargetId(kind, target.questionId),
          'delete',
        );
      });
      return true;
    },
    [markUnitChanged, openEditMode],
  );

  const duplicateQuestion = useCallback(
    (kind: QuestionSectionKind, questionId: string) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return false;
      }

      if (activeEditUnitIdRef.current) {
        return false;
      }

      const currentEditor = editorRef.current;
      const questions = getQuestionsForKind(currentEditor, kind);
      const questionIndex = findQuestionIndexById(questions, questionId);
      const sourceQuestion = questionIndex >= 0
        ? questions[questionIndex]
        : undefined;

      if (!sourceQuestion) {
        return false;
      }

      const duplicate = createDuplicateQuestion(sourceQuestion);

      currentEditor.addQuestion(kind, duplicate, questionIndex + 1);
      markUnitChanged(getQuestionEditTargetId(kind, duplicate.id), 'insert');
      return true;
    },
    [markUnitChanged, openEditMode],
  );

  const moveQuestion = useCallback(
    (kind: QuestionSectionKind, fromIndex: number, toIndex: number) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return false;
      }

      if (activeEditUnitIdRef.current) {
        return false;
      }

      const currentEditor = editorRef.current;
      const questions = getQuestionsForKind(currentEditor, kind);
      const question = questions[fromIndex];

      if (!question || fromIndex === toIndex) {
        return false;
      }

      currentEditor.reorderQuestions(kind, fromIndex, toIndex);
      markUnitChanged(getQuestionEditTargetId(kind, question.id), 'reorder');
      return true;
    },
    [markUnitChanged, openEditMode],
  );

  const insertUnitAtDivider = useCallback(
    (index: number, type: AddableContentType) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return null;
      }

      const currentActiveEditUnitId = activeEditUnitIdRef.current;

      if (currentActiveEditUnitId) {
        if (parseQuestionEditTargetId(currentActiveEditUnitId)) {
          return null;
        }

        if (!commitActiveUnit()) {
          return null;
        }
      }

      const insertedUnitId = editorRef.current.insertUnitAt(index, type);

      if (insertedUnitId) {
        markUnitChanged(insertedUnitId, 'insert');
        setActiveEditUnitIdTracked(insertedUnitId);
      }

      return insertedUnitId;
    },
    [
      commitActiveUnit,
      markUnitChanged,
      openEditMode,
      setActiveEditUnitIdTracked,
    ],
  );

  const deleteUnit = useCallback(
    (unitId: string) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return false;
      }

      const currentActiveEditUnitId = activeEditUnitIdRef.current;

      if (currentActiveEditUnitId === unitId) {
        clearActiveDraft(unitId);
        setActiveEditUnitIdTracked(null);
      } else if (currentActiveEditUnitId) {
        if (parseQuestionEditTargetId(currentActiveEditUnitId)) {
          return false;
        }

        if (!commitActiveUnit()) {
          return false;
        }
      }

      editorRef.current.deleteUnit(unitId);
      markUnitChanged(unitId, 'delete');
      return true;
    },
    [
      clearActiveDraft,
      commitActiveUnit,
      markUnitChanged,
      openEditMode,
      setActiveEditUnitIdTracked,
    ],
  );

  const moveUnit = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return false;
      }

      if (activeEditUnitIdRef.current) {
        return false;
      }

      const currentEditor = editorRef.current;
      const unit = currentEditor.contentUnits[fromIndex];

      if (!unit || fromIndex === toIndex) {
        return false;
      }

      currentEditor.moveUnit(fromIndex, toIndex);
      markUnitChanged(unit.id, 'reorder');
      return true;
    },
    [markUnitChanged, openEditMode],
  );

  const moveNavEntry = useCallback(
    (sectionId: string, activeUnitId: string, overUnitId: string) => {
      if (!pageEditModeRef.current && !openEditMode()) {
        return false;
      }

      if (activeEditUnitIdRef.current) {
        return false;
      }

      const currentEditor = editorRef.current;
      const ranges = getNavParentRanges(currentEditor.contentUnits);
      const activeRange = ranges.find(
        (range) => range.parentUnitId === activeUnitId,
      );
      const overRange = ranges.find(
        (range) => range.parentUnitId === overUnitId,
      );

      if (
        !activeRange ||
        !overRange ||
        activeRange.sectionId !== sectionId ||
        overRange.sectionId !== sectionId ||
        activeRange.parentUnitId === overRange.parentUnitId
      ) {
        return false;
      }

      currentEditor.replaceContentUnits(
        moveContentUnitRange(currentEditor.contentUnits, activeRange, overRange),
      );
      markUnitChanged(activeUnitId, 'reorder');
      return true;
    },
    [markUnitChanged, openEditMode],
  );

  const closeEditMode = useCallback(() => {
    clearActiveEditState();
    setPageEditModeTracked(false);
  }, [clearActiveEditState, setPageEditModeTracked]);

  const discardAllChanges = useCallback(() => {
    if (baselineRef.current) {
      editorRef.current.replaceState(cloneEditorState(baselineRef.current));
    } else {
      editorRef.current.resetToBaseComponent();
    }

    setLocalChanges(new Map());
    localChangesSizeRef.current = 0;
    clearActiveEditState();
    setPageEditModeTracked(false);
    baselineRef.current = null;
  }, [clearActiveEditState, setPageEditModeTracked]);

  const saveAllChanges = useCallback(async (savedState?: EditorState) => {
    setLocalChanges(new Map());
    localChangesSizeRef.current = 0;
    clearActiveEditState();
    baselineRef.current = savedState
      ? cloneEditorState(savedState)
      : editorRef.current.editorState
        ? cloneEditorState(editorRef.current.editorState)
        : null;
  }, [clearActiveEditState]);

  const hasCommittedLocalChanges = editor.isDirty || localChanges.size > 0;
  const unitDrafts = unitDraftsRef.current;
  const activeVideoDraft =
    videoEditTargetId && activeEditUnitId === videoEditTargetId
      ? videoDraftRef.current
      : null;
  const activeQuestionEditTarget = parseQuestionEditTargetId(activeEditUnitId);
  const activeQuestionIndex = activeQuestionEditTarget
    ? findQuestionIndexById(
        getQuestionsForKind(editor, activeQuestionEditTarget.kind),
        activeQuestionEditTarget.questionId,
      )
    : -1;
  const activeQuestion =
    activeQuestionEditTarget && activeQuestionIndex >= 0
      ? getQuestionsForKind(editor, activeQuestionEditTarget.kind)[
          activeQuestionIndex
        ]
      : undefined;
  // The label a student and the question cards see, NOT the array position.
  // With user-defined assignment the two differ by however many unassigned
  // questions sit earlier in the array, which is why the edit dialog used to
  // title itself "Question 38" for the card labelled "Question 30".
  const activeQuestionLabel =
    activeQuestionEditTarget && activeQuestionIndex >= 0
      ? buildQuestionDisplayList(
          getQuestionsForKind(editor, activeQuestionEditTarget.kind),
          getQuestionSettingsForEditor(
            editor.editorState?.baseComponent,
            activeQuestionEditTarget.kind === 'preLecture'
              ? 'preLecture'
              : 'homework',
          ),
        ).find((item) => item.arrayIndex === activeQuestionIndex)?.displayLabel
      : undefined;
  const activeDraft =
    activeEditUnitId !== null ? unitDrafts.get(activeEditUnitId) : undefined;
  const activeSourceUnit =
    activeEditUnitId !== null
      ? editor.contentUnits.find((candidate) => candidate.id === activeEditUnitId)
      : undefined;
  const isVideoActive =
    Boolean(videoEditTargetId) && activeEditUnitId === videoEditTargetId;
  const hasActiveDraftChanges = isVideoActive
    ? Boolean(
        activeVideoDraft && !areVideoIdsEqual(activeVideoDraft, editor.videoIds),
      )
    : activeQuestionEditTarget
      ? false
    : activeEditUnitId !== null && !areUnitsEqual(activeDraft, activeSourceUnit);
  const hasUnsavedChanges =
    hasCommittedLocalChanges || hasActiveDraftChanges;

  const value = useMemo(
    () => ({
      activeEditUnitId,
      activeQuestion,
      activeQuestionEditTarget,
      activeQuestionIndex,
      activeQuestionLabel,
      addVideoToDraft,
      autoSaveAndOpenUnit,
      cancelActiveUnit,
      cancelActiveQuestion,
      closeEditMode,
      clearMultiSelect,
      commitActiveUnit,
      discardAllChanges,
      deleteUnit,
      deleteQuestion,
      deleteQuestions,
      duplicateQuestion,
      editMode: pageEditMode,
      editor,
      hasActiveUnitDraft: Boolean(
        activeEditUnitId &&
          (unitDrafts.has(activeEditUnitId) ||
            (videoEditTargetId === activeEditUnitId && videoDraftRef.current) ||
            activeQuestionEditTarget),
      ),
      hasActiveDraftChanges,
      hasLocalChanges: hasCommittedLocalChanges,
      hasUnsavedChanges,
      insertUnitAtDivider,
      insertQuestionAt,
      isAnyUnitActive: activeEditUnitId !== null,
      leaveRefusals,
      localChanges,
      moveNavEntry,
      moveQuestion,
      moveUnit,
      multiSelectState,
      openEditMode,
      openQuestionEdit,
      openVideoEdit,
      enterMultiSelect,
      removeVideoFromDraft,
      reorderVideoDraft,
      saveAllChanges,
      saveActiveQuestion,
      setUnitDraft,
      setUnitWait,
      setVideoDraft,
      isMultiSelectActiveForScope,
      unitDrafts,
      toggleMultiSelectRow,
      videoDraftIds: getVideoDraftIds(),
      videoEditTargetId,
    }),
    [
      activeEditUnitId,
      activeQuestion,
      activeQuestionEditTarget,
      activeQuestionIndex,
      addVideoToDraft,
      autoSaveAndOpenUnit,
      cancelActiveUnit,
      cancelActiveQuestion,
      closeEditMode,
      clearMultiSelect,
      commitActiveUnit,
      discardAllChanges,
      deleteUnit,
      deleteQuestion,
      deleteQuestions,
      duplicateQuestion,
      editor,
      getVideoDraftIds,
      hasActiveDraftChanges,
      hasCommittedLocalChanges,
      hasUnsavedChanges,
      insertQuestionAt,
      insertUnitAtDivider,
      leaveRefusals,
      localChanges,
      moveNavEntry,
      moveQuestion,
      moveUnit,
      multiSelectState,
      openEditMode,
      openQuestionEdit,
      openVideoEdit,
      enterMultiSelect,
      pageEditMode,
      removeVideoFromDraft,
      reorderVideoDraft,
      saveAllChanges,
      saveActiveQuestion,
      setUnitDraft,
      setUnitWait,
      setVideoDraft,
      isMultiSelectActiveForScope,
      toggleMultiSelectRow,
      unitDraftsRevision,
      videoDraftRevision,
      videoEditTargetId,
    ],
  );

  return value;
}

export type ContentEditModeContextValue = ReturnType<
  typeof useContentEditModeController
>;

type PageEditModeContextValue = Pick<
  ContentEditModeContextValue,
  'closeEditMode' | 'editMode' | 'openEditMode'
>;

type ActiveEditContextValue = Pick<
  ContentEditModeContextValue,
  | 'activeEditUnitId'
  | 'activeQuestion'
  | 'activeQuestionEditTarget'
  | 'activeQuestionIndex'
  | 'activeQuestionLabel'
  | 'addVideoToDraft'
  | 'autoSaveAndOpenUnit'
  | 'cancelActiveQuestion'
  | 'cancelActiveUnit'
  | 'commitActiveUnit'
  | 'deleteQuestion'
  | 'deleteQuestions'
  | 'duplicateQuestion'
  | 'editor'
  | 'hasActiveDraftChanges'
  | 'hasActiveUnitDraft'
  | 'insertQuestionAt'
  | 'insertUnitAtDivider'
  | 'isAnyUnitActive'
  | 'leaveRefusals'
  | 'moveQuestion'
  | 'openQuestionEdit'
  | 'openVideoEdit'
  | 'removeVideoFromDraft'
  | 'reorderVideoDraft'
  | 'saveActiveQuestion'
  | 'setUnitDraft'
  | 'setUnitWait'
  | 'setVideoDraft'
  | 'unitDrafts'
  | 'videoDraftIds'
  | 'videoEditTargetId'
>;

type LocalChangesContextValue = Pick<
  ContentEditModeContextValue,
  | 'discardAllChanges'
  | 'hasLocalChanges'
  | 'hasUnsavedChanges'
  | 'localChanges'
  | 'saveAllChanges'
>;

export interface MultiSelectContextValue {
  clearSelection: (scope?: string) => void;
  enterSelection: (scope: string, initialRowId: string) => void;
  isSelectionActiveForScope: (scope: string) => boolean;
  selectMultipleMode: boolean;
  selectMultipleScope: string | null;
  selectedCount: number;
  selectedRowIds: ReadonlySet<string>;
  toggleRowSelection: (scope: string, rowId: string) => void;
}

const PageEditModeContext =
  createContext<PageEditModeContextValue | null>(null);
const ActiveEditContext = createContext<ActiveEditContextValue | null>(null);
const LocalChangesContext =
  createContext<LocalChangesContextValue | null>(null);
const MultiSelectContext =
  createContext<MultiSelectContextValue | null>(null);

const emptySelectedRowIds = new Set<string>();
const noopClearSelection = () => undefined;
const noopEnterSelection = () => undefined;
const noopIsSelectionActiveForScope = () => false;
const noopToggleRowSelection = () => undefined;

export function ContentEditModeProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: ContentEditModeContextValue;
}) {
  const pageEditModeValue = useMemo(
    () => ({
      closeEditMode: value.closeEditMode,
      editMode: value.editMode,
      openEditMode: value.openEditMode,
    }),
    [value.closeEditMode, value.editMode, value.openEditMode],
  );
  const activeEditValue = useMemo(
    () => ({
      activeEditUnitId: value.activeEditUnitId,
      activeQuestion: value.activeQuestion,
      activeQuestionEditTarget: value.activeQuestionEditTarget,
      activeQuestionIndex: value.activeQuestionIndex,
      activeQuestionLabel: value.activeQuestionLabel,
      addVideoToDraft: value.addVideoToDraft,
      autoSaveAndOpenUnit: value.autoSaveAndOpenUnit,
      cancelActiveQuestion: value.cancelActiveQuestion,
      cancelActiveUnit: value.cancelActiveUnit,
      commitActiveUnit: value.commitActiveUnit,
      deleteQuestion: value.deleteQuestion,
      deleteQuestions: value.deleteQuestions,
      duplicateQuestion: value.duplicateQuestion,
      editor: value.editor,
      hasActiveDraftChanges: value.hasActiveDraftChanges,
      hasActiveUnitDraft: value.hasActiveUnitDraft,
      insertQuestionAt: value.insertQuestionAt,
      insertUnitAtDivider: value.insertUnitAtDivider,
      isAnyUnitActive: value.isAnyUnitActive,
      leaveRefusals: value.leaveRefusals,
      moveQuestion: value.moveQuestion,
      openQuestionEdit: value.openQuestionEdit,
      openVideoEdit: value.openVideoEdit,
      removeVideoFromDraft: value.removeVideoFromDraft,
      reorderVideoDraft: value.reorderVideoDraft,
      saveActiveQuestion: value.saveActiveQuestion,
      setUnitDraft: value.setUnitDraft,
      setUnitWait: value.setUnitWait,
      setVideoDraft: value.setVideoDraft,
      unitDrafts: value.unitDrafts,
      videoDraftIds: value.videoDraftIds,
      videoEditTargetId: value.videoEditTargetId,
    }),
    [
      value.activeEditUnitId,
      value.activeQuestion,
      value.activeQuestionEditTarget,
      value.activeQuestionIndex,
      value.activeQuestionLabel,
      value.addVideoToDraft,
      value.autoSaveAndOpenUnit,
      value.cancelActiveQuestion,
      value.cancelActiveUnit,
      value.commitActiveUnit,
      value.deleteQuestion,
      value.deleteQuestions,
      value.duplicateQuestion,
      value.editor,
      value.hasActiveDraftChanges,
      value.hasActiveUnitDraft,
      value.insertQuestionAt,
      value.insertUnitAtDivider,
      value.isAnyUnitActive,
      value.leaveRefusals,
      value.moveQuestion,
      value.openQuestionEdit,
      value.openVideoEdit,
      value.removeVideoFromDraft,
      value.reorderVideoDraft,
      value.saveActiveQuestion,
      value.setUnitDraft,
      value.setUnitWait,
      value.setVideoDraft,
      value.unitDrafts,
      value.videoDraftIds,
      value.videoEditTargetId,
    ],
  );
  const localChangesValue = useMemo(
    () => ({
      discardAllChanges: value.discardAllChanges,
      hasLocalChanges: value.hasLocalChanges,
      hasUnsavedChanges: value.hasUnsavedChanges,
      localChanges: value.localChanges,
      saveAllChanges: value.saveAllChanges,
    }),
    [
      value.discardAllChanges,
      value.hasLocalChanges,
      value.hasUnsavedChanges,
      value.localChanges,
      value.saveAllChanges,
    ],
  );
  const multiSelectState = value.multiSelectState ?? {
    selectMultipleMode: false,
    selectMultipleScope: null,
    selectedRowIds: emptySelectedRowIds,
  };
  const multiSelectValue = useMemo(
    () => ({
      clearSelection: value.clearMultiSelect ?? noopClearSelection,
      enterSelection: value.enterMultiSelect ?? noopEnterSelection,
      isSelectionActiveForScope:
        value.isMultiSelectActiveForScope ?? noopIsSelectionActiveForScope,
      selectMultipleMode: multiSelectState.selectMultipleMode,
      selectMultipleScope: multiSelectState.selectMultipleScope,
      selectedCount: multiSelectState.selectedRowIds.size,
      selectedRowIds: multiSelectState.selectedRowIds,
      toggleRowSelection:
        value.toggleMultiSelectRow ?? noopToggleRowSelection,
    }),
    [
      value.clearMultiSelect,
      value.enterMultiSelect,
      value.isMultiSelectActiveForScope,
      multiSelectState.selectMultipleMode,
      multiSelectState.selectMultipleScope,
      multiSelectState.selectedRowIds,
      value.toggleMultiSelectRow,
    ],
  );

  return (
    <PageEditModeContext.Provider value={pageEditModeValue}>
      <ActiveEditContext.Provider value={activeEditValue}>
        <LocalChangesContext.Provider value={localChangesValue}>
          <MultiSelectContext.Provider value={multiSelectValue}>
            {children}
          </MultiSelectContext.Provider>
        </LocalChangesContext.Provider>
      </ActiveEditContext.Provider>
    </PageEditModeContext.Provider>
  );
}

function useRequiredContext<T>(contextValue: T | null, hookName: string) {
  if (!contextValue) {
    throw new Error(`${hookName} must be used within ContentEditModeProvider.`);
  }

  return contextValue;
}

export function usePageEditMode() {
  return useRequiredContext(useContext(PageEditModeContext), 'usePageEditMode');
}

export function useOptionalPageEditMode() {
  return useContext(PageEditModeContext);
}

export function useActiveEdit() {
  return useRequiredContext(useContext(ActiveEditContext), 'useActiveEdit');
}

export function useOptionalActiveEdit() {
  return useContext(ActiveEditContext);
}

export function useMultiSelect() {
  return useRequiredContext(useContext(MultiSelectContext), 'useMultiSelect');
}

export function useOptionalMultiSelect() {
  return useContext(MultiSelectContext);
}

export function useUnitEditState(unitId: string) {
  const activeEdit = useActiveEdit();
  const isActive = activeEdit.activeEditUnitId === unitId;
  const isDragDisabled = activeEdit.isAnyUnitActive;

  return {
    disabledReason: isDragDisabled
      ? 'Save or cancel current edit first'
      : undefined,
    isActive,
    isDragDisabled,
    isEditableCollapsed: false,
    open: () => activeEdit.autoSaveAndOpenUnit(unitId),
  };
}

export function useLocalChanges() {
  const localChanges = useRequiredContext(
    useContext(LocalChangesContext),
    'useLocalChanges',
  );

  return {
    discardAllChanges: localChanges.discardAllChanges,
    hasLocalChanges: localChanges.hasLocalChanges,
    hasUnsavedChanges: localChanges.hasUnsavedChanges,
    localChanges: localChanges.localChanges,
    saveAllChanges: localChanges.saveAllChanges,
  };
}
